import React, { useState, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import type { OpenTab } from '@/components/features/document-tabs'
import { fetchWithRetry } from '@/components/layout/resizable-layout.utils'
import { isChatConversationTabId, isPathKeyedUnresolvedDocumentId } from '@/components/layout/document-viewer.utils'
import { isBasecampIntegrationTabId } from '@/lib/basecamp-tab-routing'
import { isEditHistoryTabId } from '@/lib/static-app-views'
import { fetchWithAuth } from '@/lib/api'
import {
  documentUrlForPathOrId,
  isUuid,
  buildStoredDocumentUrl,
  buildAppUrl,
  resolveStoragePathForFetch,
} from '@/lib/app-routes'
import { markProgrammaticNavigation } from '@/hooks/use-url-sync'
import {
  tabMatchesDeletedDocument,
  markDocumentRecentlyDeleted,
  type DocumentDeletedDetail,
} from '@/lib/document-delete-utils'
import { mountPrintContainer, resolvePrintSource } from '@/lib/print-document'

/** Parent directory for a content-relative file path; null for root-level files or unknown path. */
function parentFolderPathFromContentPath(filePath: string | null | undefined): string | null {
  if (!filePath || !filePath.includes('/')) return null
  const i = filePath.lastIndexOf('/')
  return i > 0 ? filePath.slice(0, i) : null
}

/**
 * Document save state
 */
export interface DocumentSaveState {
  saving: boolean
  isDeleted: boolean
}

/**
 * Undo state
 */
export interface UndoState {
  undoing: boolean
  hasHistory: boolean
  lastUndo: Date | null
}

/**
 * Document to delete
 */
export interface DocumentToDelete {
  id: string
  title: string
}

/**
 * Return type for use-document-operations hook
 */
export interface UseDocumentOperationsReturn {
  // State
  currentDocumentPath: string | null
  documentSaveState: DocumentSaveState
  undoState: UndoState
  moveDialogOpen: boolean
  renameDialogOpen: boolean
  deleteDialogOpen: boolean
  documentToDelete: DocumentToDelete | null

  // Setters
  setCurrentDocumentPath: (path: string | null) => void
  setMoveDialogOpen: (open: boolean) => void
  setRenameDialogOpen: (open: boolean) => void
  setDeleteDialogOpen: (open: boolean) => void

  // Handlers
  handleRenameClick: () => void
  handleRenameConfirm: (
    newFilename: string,
    options?: { title?: string; skipFilename?: boolean },
  ) => Promise<void>
  handleMove: (newFolderPath: string) => Promise<void>
  handleDeleteClick: () => void
  handleDelete: () => Promise<void>
  handleShowFileLocation: () => void
  handleManualSave: () => void
  /** Resets toolbar save spinner; invoked from DocumentContent after save attempt. */
  completeManualSave: () => void
  handleUndo: () => Promise<void>
  handleExportPDF: () => void
  handleAddToChatClick: () => Promise<void>
}

/**
 * Hook for managing document operations (rename, move, delete, save, undo, etc.)
 */
export function useDocumentOperations({
  openTabs,
  setOpenTabs,
  activeTabId,
  setActiveTabId,
  onDocumentDragToChat,
  onShowFileLocation,
  parentDocumentClick,
  handleDocumentClick,
  navigation,
  getWorkspaceIdForPath,
  setSelectedProjectId,
  setShowDocumentList,
  workspacesRoute = [],
}: {
  openTabs: OpenTab[]
  setOpenTabs: React.Dispatch<React.SetStateAction<OpenTab[]>>
  activeTabId: string
  setActiveTabId: (id: string) => void
  onDocumentDragToChat?: (tabId: string, title: string, type: OpenTab["type"]) => void
  onShowFileLocation?: (folderPath: string) => void
  parentDocumentClick?: (documentId: string, event?: React.MouseEvent) => Promise<void>
  handleDocumentClick: (documentId: string, openInNewTab?: boolean) => Promise<void>
  navigation?: ReturnType<typeof import('@/hooks/use-navigation-history').usePerTabNavigationHistory>
  getWorkspaceIdForPath?: (path: string) => string | undefined
  setSelectedProjectId?: (id: string | null) => void
  setShowDocumentList?: (show: boolean) => void
  workspacesRoute?: import('@/lib/app-routes').WorkspaceRouteInfo[]
}): UseDocumentOperationsReturn {
  const navigate = useNavigate()

  const navigateAfterDeletingLastOpenDocumentTab = useCallback(
    (filePathHint: string | null | undefined) => {
      const parentPath = parentFolderPathFromContentPath(filePathHint)
      const folderForExplore = parentPath ?? ''
      markProgrammaticNavigation()
      const targetUrl = folderForExplore
        ? documentUrlForPathOrId(folderForExplore, workspacesRoute)
        : '/'
      navigate(targetUrl)
      const wsId = getWorkspaceIdForPath?.(folderForExplore)
      const explorerTab: OpenTab = {
        id: 'list',
        title: 'Explorer',
        type: 'explorer',
        isDirty: false,
        isPinned: false,
        tabSlotId: 'tab-main',
        explorerData: { folderPath: folderForExplore || null },
        ...(wsId != null ? { workspaceId: wsId } : {}),
      }
      setOpenTabs([explorerTab])
      setActiveTabId('list')
      setSelectedProjectId?.(folderForExplore || null)
      setShowDocumentList?.(true)
      navigation?.navigate(
        'tab-main',
        { documentId: null, folderPath: folderForExplore || null, viewMode: 'list' },
        'deleted-last-open-tab-show-parent'
      )
      if (folderForExplore) {
        window.dispatchEvent(
          new CustomEvent('show-folder-in-browse', { detail: { folderPath: folderForExplore } })
        )
      }
    },
    [
      navigate,
      setOpenTabs,
      setActiveTabId,
      getWorkspaceIdForPath,
      setSelectedProjectId,
      setShowDocumentList,
      navigation,
      workspacesRoute,
    ]
  )

  // State for current document path
  const [currentDocumentPath, setCurrentDocumentPath] = useState<string | null>(null)

  // State for dialogs
  const [moveDialogOpen, setMoveDialogOpen] = useState(false)
  const [renameDialogOpen, setRenameDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [documentToDelete, setDocumentToDelete] = useState<DocumentToDelete | null>(null)

  // State for save operations
  const [documentSaveState, setDocumentSaveState] = useState<DocumentSaveState>({
    saving: false,
    isDeleted: false
  })

  // State for undo operations
  const [undoState, setUndoState] = useState<UndoState>({
    undoing: false,
    hasHistory: false,
    lastUndo: null
  })

  // Check if document has edit history
  useEffect(() => {
    const checkHistory = async () => {
      // Skip special view IDs and new documents
      const currentTab = openTabs.find(tab => tab.id === activeTabId)
      if (!activeTabId ||
          activeTabId === 'list' ||
          activeTabId === 'home' ||
          activeTabId === 'search-view' ||
          activeTabId === 'people-index' ||
          activeTabId.startsWith('person-view-') ||
          isEditHistoryTabId(activeTabId) ||
          currentTab?.type === 'explorer' ||
          isPathKeyedUnresolvedDocumentId(activeTabId) ||
          currentTab?.type === 'home' ||
          currentTab?.type === 'search-view' ||
          currentTab?.type === 'person-view' ||
          currentTab?.type === 'edit-history' ||
          currentTab?.type === 'inbox' ||
          currentTab?.type === 'inbox-thread' ||
          isChatConversationTabId(activeTabId) ||
          activeTabId.startsWith('inbox-thread-') ||
          activeTabId === 'graph' ||
          activeTabId.startsWith('new-') ||
          activeTabId.startsWith('view:') ||
          currentTab?.type === 'graph-view') {
        setUndoState(prev => ({ ...prev, hasHistory: false }))
        return
      }

      try {
        if (!activeTabId || activeTabId === 'null' || activeTabId === 'undefined') {
          setUndoState(prev => ({ ...prev, hasHistory: false }))
          return
        }

        // Skip history check for integration tabs (not document IDs)
        if (activeTabId && (
            isBasecampIntegrationTabId(activeTabId) ||
            activeTabId.startsWith('integration-') ||
            activeTabId.startsWith('gdrive-preview-') ||
            activeTabId.startsWith('workspace-home-') ||
            isChatConversationTabId(activeTabId)
          )) {
          setUndoState(prev => ({ ...prev, hasHistory: false }))
          return
        }

        // Check if activeTabId is a UUID or file path
        const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(activeTabId)

        let documentId = activeTabId

        // If it's a file path, resolve to UUID first
        if (!isUUID) {
          const storagePath = resolveStoragePathForFetch(activeTabId, workspacesRoute)
          const docResponse = await fetchWithRetry(`/api/documents/by-path?path=${encodeURIComponent(storagePath)}`, {
            retryOn404: true,
            max404Retries: 2 // Only retry 1-2 times for indexing delays
          })
          if (!docResponse.ok) {
            // Document not found (likely deleted) - handle gracefully
            if (docResponse.status === 404) {
              // Don't log as error - this is expected when files are deleted
              console.log(`[DocumentViewer] Document not found (404): ${activeTabId} - file may have been deleted`)
            } else {
              console.log('[DocumentViewer] Could not resolve file path to UUID:', activeTabId)
            }
            setUndoState(prev => ({ ...prev, hasHistory: false }))
            return
          }
          const docData = await docResponse.json()
          documentId = docData.id
        }

        // Now fetch history with the UUID
        const response = await fetchWithAuth(`/api/documents/${documentId}/history?limit=1`)
        if (response.ok) {
          const data = await response.json()
          setUndoState(prev => ({
            ...prev,
            hasHistory: data.history && data.history.length > 0
          }))
        } else {
          setUndoState(prev => ({ ...prev, hasHistory: false }))
        }
      } catch (error) {
        console.error('Error checking history:', error)
        setUndoState(prev => ({ ...prev, hasHistory: false }))
      }
    }

    checkHistory()
  }, [activeTabId, openTabs, undoState.lastUndo])

  // Listen for tab context menu triggers (rename, move, export PDF)
  useEffect(() => {
    const handleTriggerRename = (event: CustomEvent<{ tabId: string }>) => {
      if (event.detail.tabId === activeTabId) {
        handleRenameClick()
      } else {
        // Switch to tab first
        setActiveTabId(event.detail.tabId)
        // Wait a bit for tab to switch, then trigger rename
        setTimeout(() => {
          handleRenameClick()
        }, 100)
      }
    }

    const handleTriggerMove = (event: CustomEvent<{ tabId: string }>) => {
      if (event.detail.tabId === activeTabId && currentDocumentPath) {
        setMoveDialogOpen(true)
      } else {
        // Switch to tab first
        setActiveTabId(event.detail.tabId)
        // Wait a bit for tab to switch, then trigger move
        setTimeout(() => {
          if (currentDocumentPath) {
            setMoveDialogOpen(true)
          }
        }, 100)
      }
    }

    const handleTriggerExportPDF = (event: CustomEvent<{ tabId: string }>) => {
      if (event.detail.tabId === activeTabId) {
        handleExportPDF()
      } else {
        // Switch to tab first
        setActiveTabId(event.detail.tabId)
        // Wait a bit for tab to switch, then trigger export
        setTimeout(() => {
          handleExportPDF()
        }, 100)
      }
    }

    window.addEventListener('trigger-rename', handleTriggerRename as EventListener)
    window.addEventListener('trigger-move', handleTriggerMove as EventListener)
    window.addEventListener('trigger-export-pdf', handleTriggerExportPDF as EventListener)

    return () => {
      window.removeEventListener('trigger-rename', handleTriggerRename as EventListener)
      window.removeEventListener('trigger-move', handleTriggerMove as EventListener)
      window.removeEventListener('trigger-export-pdf', handleTriggerExportPDF as EventListener)
    }
  }, [activeTabId, currentDocumentPath, setActiveTabId, setMoveDialogOpen])

  // Close tab when document is deleted (e.g. from sidebar or 404 from poll)
  useEffect(() => {
    const handler = (event: CustomEvent<DocumentDeletedDetail>) => {
      const detail = event.detail || {}
      const tabToClose = openTabs.find((t) => tabMatchesDeletedDocument(t, detail))
      if (!tabToClose) return

      const updatedTabs = openTabs.filter((tab) => tab.id !== tabToClose.id)
      if (activeTabId === tabToClose.id) {
        if (updatedTabs.length > 0) {
          // Prefer a stable tab (Home) when present; otherwise the previous strip neighbor.
          const newActive =
            updatedTabs.find((t) => t.id === 'home' || t.type === 'home') ??
            updatedTabs[updatedTabs.length - 1]
          // Must use buildAppUrl (not documentUrlForPathOrId(tabId)) so Activity/Home
          // become /search?view=activity or /, not /search-view or /home. Without
          // markProgrammaticNavigation + replace, the stale deleted-doc URL races
          // URL sync and reopens the tab immediately.
          markProgrammaticNavigation()
          setOpenTabs(updatedTabs)
          setActiveTabId(newActive.id)
          navigate(buildAppUrl({ tab: newActive, workspaces: workspacesRoute }), {
            replace: true,
          })
        } else {
          const filePathHint =
            (detail.path && detail.path.includes('/') ? detail.path : undefined) ??
            (detail.filePath && detail.filePath.includes('/') ? detail.filePath : undefined) ??
            (detail.documentId && detail.documentId.includes('/') ? detail.documentId : undefined) ??
            (tabToClose.filePath && tabToClose.filePath.includes('/') ? tabToClose.filePath : undefined) ??
            (tabToClose.id.includes('/') ? tabToClose.id : undefined)
          navigateAfterDeletingLastOpenDocumentTab(filePathHint ?? null)
        }
      } else {
        setOpenTabs(updatedTabs)
      }
    }
    window.addEventListener('document-deleted', handler as EventListener)
    return () => window.removeEventListener('document-deleted', handler as EventListener)
  }, [openTabs, activeTabId, setOpenTabs, setActiveTabId, navigate, navigateAfterDeletingLastOpenDocumentTab, workspacesRoute])

  // Sidebar (and other) renames dispatch document-renamed but do not own tab state — keep tabs/URL aligned.
  useEffect(() => {
    const handler = (event: Event) => {
      const { oldPath, newPath } = (event as CustomEvent<{ oldPath: string; newPath: string }>).detail || {}
      if (!oldPath || !newPath || oldPath === newPath) return

      setOpenTabs((prev) =>
        prev.map((tab) => {
          if (tab.id !== oldPath && tab.filePath !== oldPath) return tab
          return {
            ...tab,
            ...(isUuid(tab.id) ? {} : { id: newPath }),
            filePath: newPath,
          }
        })
      )

      const activeTab = openTabs.find((tab) => tab.id === activeTabId)
      const activeMatchesPath =
        activeTabId === oldPath ||
        (activeTab?.filePath === oldPath)

      if (activeMatchesPath) {
        markProgrammaticNavigation()
        const nextActiveId = activeTab && isUuid(activeTab.id) ? activeTab.id : newPath
        navigate(
          activeTab && isUuid(activeTab.id)
            ? buildStoredDocumentUrl({ ...activeTab, filePath: newPath }, workspacesRoute)
            : documentUrlForPathOrId(newPath, workspacesRoute)
        )
        if (!isUuid(activeTabId ?? '')) {
          setActiveTabId(newPath)
        }
      }

      setCurrentDocumentPath((prev) => (prev === oldPath ? newPath : prev))
    }
    window.addEventListener('document-renamed', handler as EventListener)
    return () => window.removeEventListener('document-renamed', handler as EventListener)
  }, [navigate, setOpenTabs, setActiveTabId, activeTabId, openTabs, setCurrentDocumentPath])

  const handleRenameClick = useCallback(() => {
    const currentTab = openTabs.find(tab => tab.id === activeTabId)
    if (!currentDocumentPath || currentTab?.type === 'explorer') return
    setRenameDialogOpen(true)
  }, [currentDocumentPath, activeTabId, openTabs])

  const handleRenameConfirm = useCallback(async (
    newFilename: string,
    options?: { title?: string; skipFilename?: boolean },
  ) => {
    const currentTab = openTabs.find(tab => tab.id === activeTabId)
    if (!currentDocumentPath || currentTab?.type === 'explorer') return

    try {
      const pathFallback = currentDocumentPath || currentTab?.filePath || null
      const { renameDocument, updateDocument } = await import('../lib/api')
      const finalFilename = newFilename.trim().endsWith('.md')
        ? newFilename.trim()
        : `${newFilename.trim()}.md`
      const oldFilename = currentDocumentPath.split('/').pop() || ''
      const oldBase = oldFilename.replace(/\.md$/i, '')
      const newBase = finalFilename.replace(/\.md$/i, '')
      const filenameChanged = !options?.skipFilename && oldBase !== newBase

      let renameResult: {
        success: boolean
        document: { id: string; oldPath: string; newPath: string }
      } | null = null

      if (filenameChanged) {
        renameResult = await renameDocument(activeTabId, finalFilename, { pathFallback })
      }

      const docIdForTitle =
        renameResult?.document?.id ||
        (activeTabId && /^[0-9a-f-]{36}$/i.test(activeTabId) ? activeTabId : null)

      if (options?.title?.trim()) {
        const titleId = docIdForTitle || activeTabId
        await updateDocument(
          titleId,
          {
            title: options.title.trim(),
            frontmatter: { title: options.title.trim() },
            writeToFile: true,
          },
          { pathFallback: renameResult?.document?.newPath || pathFallback },
        )
      }

      if (renameResult?.success && renameResult.document.newPath) {
        const updatedTabs = openTabs.map(tab =>
          tab.id === activeTabId
            ? {
                ...tab,
                id: renameResult!.document.id || renameResult!.document.newPath,
                filePath: renameResult!.document.newPath,
                title: options?.title?.trim() || tab.title,
              }
            : tab
        )
        setOpenTabs(updatedTabs)
        setActiveTabId(renameResult.document.id || renameResult.document.newPath)
        setCurrentDocumentPath(renameResult.document.newPath)

        markProgrammaticNavigation()
        navigate(documentUrlForPathOrId(renameResult.document.newPath, workspacesRoute))

        window.dispatchEvent(new CustomEvent('document-renamed', {
          detail: {
            oldPath: renameResult.document.oldPath,
            newPath: renameResult.document.newPath
          }
        }))

        toast.success('Document renamed', {
          description: `${oldFilename} → ${finalFilename}`
        })
      } else if (options?.title?.trim()) {
        const updatedTabs = openTabs.map(tab =>
          tab.id === activeTabId
            ? { ...tab, title: options.title!.trim() }
            : tab
        )
        setOpenTabs(updatedTabs)
        window.dispatchEvent(new CustomEvent('document-updated', {
          detail: { id: activeTabId, title: options.title.trim() }
        }))
        toast.success('Document title updated')
      }
    } catch (error) {
      console.error('Failed to rename document:', error)
      throw error // Let dialog handle error display
    }
  }, [activeTabId, currentDocumentPath, openTabs, setOpenTabs, setActiveTabId, navigate])

  const handleMove = useCallback(async (newFolderPath: string) => {
    const currentTab = openTabs.find(tab => tab.id === activeTabId)
    if (!activeTabId || currentTab?.type === 'explorer' || !currentDocumentPath) return

    try {
      const pathFallback = currentDocumentPath || currentTab?.filePath || null
      const { resolveDocumentIdForMutation } = await import('../lib/api')
      const documentId = await resolveDocumentIdForMutation(activeTabId, pathFallback)

      // Extract filename from current path
      const filename = currentDocumentPath.split('/').pop() || 'document.md'
      const newPath = newFolderPath ? `${newFolderPath}/${filename}` : filename

      // Call move API
      const response = await fetchWithAuth(`/api/documents/${documentId}/move`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newPath })
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to move document')
      }

      const result = await response.json()

      // Close dialog
      setMoveDialogOpen(false)

      // Show success toast with undo
      toast.success('Document moved', {
        description: `Moved to ${newPath}`,
        duration: 10000,
        action: {
          label: 'Undo',
          onClick: async () => {
            try {
              // Move back to original location
              const undoResponse = await fetchWithAuth(`/api/documents/${documentId}/move`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ newPath: result.document.oldPath })
              })

              if (undoResponse.ok) {
                const undoResult = await undoResponse.json()
                toast.success('Move undone', {
                  description: 'Document restored to original location'
                })

                // Refresh file tree in sidebar
                window.dispatchEvent(new CustomEvent('document-moved', {
                  detail: { oldPath: undoResult.document.newPath, newPath: undoResult.document.oldPath }
                }))

                // Reload document if still open
                window.dispatchEvent(new CustomEvent('document-updated', {
                  detail: { documentId: activeTabId }
                }))
              }
            } catch (error) {
              toast.error('Failed to undo', {
                description: error instanceof Error ? error.message : 'Unknown error'
              })
            }
          }
        }
      })

      // Update tab with new path
      const updatedTabs = openTabs.map(tab =>
        tab.id === activeTabId
          ? { ...tab, id: newPath } // Update tab ID to new path
          : tab
      )
      setOpenTabs(updatedTabs)
      setActiveTabId(newPath)

      // Refresh file tree in sidebar
      window.dispatchEvent(new CustomEvent('document-moved', {
        detail: { oldPath: result.document.oldPath, newPath: result.document.newPath }
      }))

      // Update URL
      navigate(documentUrlForPathOrId(newPath, workspacesRoute), { replace: true })

    } catch (error) {
      console.error('Move failed:', error)
      toast.error('Move Failed', {
        description: error instanceof Error ? error.message : 'Unknown error'
      })
    }
  }, [activeTabId, currentDocumentPath, openTabs, setOpenTabs, setActiveTabId, navigate, setMoveDialogOpen])

  const handleDeleteClick = useCallback(() => {
    const activeTab = openTabs.find(tab => tab.id === activeTabId)
    if (activeTab && activeTab.type !== 'explorer' && activeTab.type !== 'home' && activeTabId && !activeTabId.startsWith('new-')) {
      setDocumentToDelete({ id: activeTabId, title: activeTab.title })
      setDeleteDialogOpen(true)
    }
  }, [activeTabId, openTabs])

  const handleDelete = useCallback(async () => {
    if (!documentToDelete) return

    try {
      const { deleteDocument } = await import('../lib/api')
      await deleteDocument(documentToDelete.id)

      // Close dialog
      setDeleteDialogOpen(false)
      const deletedDoc = { ...documentToDelete }
      setDocumentToDelete(null)

      // Show success toast with undo
      toast.success('Document deleted', {
        description: `"${deletedDoc.title}" has been deleted.`,
        duration: 10000, // 10 seconds for undo
        action: {
          label: 'Undo',
          onClick: async () => {
            try {
              // Resolve document ID (could be UUID or file path)
              const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(deletedDoc.id)
              let documentId = deletedDoc.id

              if (!isUUID) {
                // Resolve file path to UUID
                const docResponse = await fetchWithAuth(`/api/documents/by-path?path=${encodeURIComponent(deletedDoc.id)}`)
                if (docResponse.ok) {
                  const docData = await docResponse.json()
                  documentId = docData.id
                }
              }

              // Restore document
              const { restoreDocument } = await import('../lib/api')
              const restoreResult = await restoreDocument(documentId)

              toast.success('Document restored', {
                description: `"${deletedDoc.title}" has been restored.`
              })

              // Get restored file path from API response
              const restoredFilePath = restoreResult.document?.filePath || restoreResult.document?.file_path || deletedDoc.id

              // Refresh file tree
              window.dispatchEvent(new CustomEvent('document-restored', {
                detail: {
                  path: restoredFilePath,
                  documentId: documentId,
                  filePath: restoredFilePath
                }
              }))

              // Open the restored document
              // Check if document is already open
              const existingTab = openTabs.find(tab => tab.id === restoredFilePath || tab.id === deletedDoc.id)

              if (existingTab) {
                // Switch to existing tab
                setActiveTabId(existingTab.id)
                // Update URL
                navigate(documentUrlForPathOrId(existingTab.filePath || existingTab.id, workspacesRoute))
                // Trigger highlight animation
                setTimeout(() => {
                  window.dispatchEvent(new CustomEvent('document-highlight', {
                    detail: { documentId: existingTab.id }
                  }))
                }, 100)
              } else {
                // Open document in new tab
                if (parentDocumentClick) {
                  await parentDocumentClick(restoredFilePath)
                  // Trigger highlight animation after document loads
                  setTimeout(() => {
                    window.dispatchEvent(new CustomEvent('document-highlight', {
                      detail: { documentId: restoredFilePath }
                    }))
                  }, 500)
                } else {
                  // Fallback: use handleDocumentClick
                  await handleDocumentClick(restoredFilePath)
                  setTimeout(() => {
                    window.dispatchEvent(new CustomEvent('document-highlight', {
                      detail: { documentId: restoredFilePath }
                    }))
                  }, 500)
                }
              }
            } catch (error) {
              toast.error('Failed to restore', {
                description: error instanceof Error ? error.message : 'Unknown error'
              })
            }
          }
        }
      })

      // Refresh file tree, document list, and browse view; single listener updates tabs + URL
      const activeTab = openTabs.find((tab) => tab.id === activeTabId)
      const path =
        currentDocumentPath ||
        activeTab?.filePath ||
        (deletedDoc.id.includes('/') ? deletedDoc.id : undefined)
      markDocumentRecentlyDeleted(deletedDoc.id, path, activeTab?.filePath ?? undefined)
      window.dispatchEvent(new CustomEvent('document-deleted', {
        detail: {
          path,
          documentId: deletedDoc.id,
          filePath: activeTab?.filePath ?? (path?.includes('/') ? path : undefined),
        }
      }))

    } catch (error) {
      console.error('Delete failed:', error)
      toast.error('Delete Failed', {
        description: error instanceof Error ? error.message : 'Unknown error'
      })
    }
  }, [documentToDelete, openTabs, setOpenTabs, activeTabId, setActiveTabId, currentDocumentPath, navigate, parentDocumentClick, handleDocumentClick])

  const handleShowFileLocation = useCallback(() => {
    if (!currentDocumentPath || !onShowFileLocation) return

    // Extract folder path (everything except the filename)
    const pathParts = currentDocumentPath.split('/')
    pathParts.pop() // Remove filename
    const folderPath = pathParts.join('/') || ''

    console.log('[Show File Location] Navigating to folder:', folderPath || '(root)')
    onShowFileLocation(folderPath)
  }, [currentDocumentPath, onShowFileLocation])

  const handleManualSave = useCallback(() => {
    // DocumentContent runs the real save when `saving` becomes true; it must call
    // `completeManualSave` in a finally block or the toolbar spinner never clears.
    setDocumentSaveState(prev => ({ ...prev, saving: true }))
  }, [])

  const completeManualSave = useCallback(() => {
    setDocumentSaveState(prev => ({ ...prev, saving: false }))
  }, [])

  const handleUndo = useCallback(async () => {
    const currentTab = openTabs.find(tab => tab.id === activeTabId)
    if (!activeTabId ||
        activeTabId === 'list' ||
        activeTabId === 'home' ||
        currentTab?.type === 'explorer' ||
        currentTab?.type === 'home' ||
        activeTabId.startsWith('new-') ||
        undoState.undoing) {
      return
    }
    // Integration tabs are not documents
    if (activeTabId === 'basecamp' || activeTabId === 'basecamp-triage' ||
        activeTabId.startsWith('basecamp-project-') || activeTabId.startsWith('basecamp-todo') ||
        activeTabId.startsWith('integration-') || activeTabId.startsWith('gdrive-preview-')) {
      return
    }

    try {
      setUndoState(prev => ({ ...prev, undoing: true }))

      // Check if activeTabId is UUID or file path
      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(activeTabId)
      let documentId = activeTabId

      // Resolve file path to UUID if needed
      if (!isUUID) {
        const storagePath = resolveStoragePathForFetch(activeTabId, workspacesRoute)
        const docResponse = await fetchWithAuth(`/api/documents/by-path?path=${encodeURIComponent(storagePath)}`)
        if (!docResponse.ok) {
          alert('Could not find document to undo')
          setUndoState(prev => ({ ...prev, undoing: false }))
          return
        }
        const docData = await docResponse.json()
        documentId = docData.id
      }

      const response = await fetchWithAuth(`/api/documents/${documentId}/undo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ writeToFile: true })
      })

      if (!response.ok) {
        const error = await response.json()
        console.error('Undo failed:', error)
        alert(`Undo failed: ${error.error || 'Unknown error'}`)
        return
      }

      const result = await response.json()

      // Trigger a refresh of the document content
      // The DocumentContent component will re-fetch on documentId change
      // So we can trigger by toggling a refresh flag or just reloading
      window.location.reload() // Simple approach - reloads the page

      setUndoState(prev => ({
        ...prev,
        undoing: false,
        lastUndo: new Date()
      }))
    } catch (error) {
      console.error('Error during undo:', error)
      alert('Undo failed. Please try again.')
      setUndoState(prev => ({ ...prev, undoing: false }))
    }
  }, [activeTabId, openTabs, undoState.undoing])

  const handleExportPDF = useCallback(() => {
    const source = resolvePrintSource()
    if (!source) {
      console.error('Print content not found')
      toast.error('Nothing to print', {
        description: 'Open the document in Visual or Source view, then try Download PDF again.',
      })
      return
    }

    const printContainer = mountPrintContainer(source)
    window.print()

    // window.print() is modal in Chromium; delay covers engines that return earlier.
    setTimeout(() => {
      printContainer.remove()
    }, 100)
  }, [])

  const handleAddToChatClick = useCallback(async () => {
    const currentTab = openTabs.find(tab => tab.id === activeTabId)
    if (!currentDocumentPath || !activeTabId || currentTab?.type === 'explorer') return

    const activeTab = openTabs.find(tab => tab.id === activeTabId)
    if (!activeTab) return

    // Add current document to chat references
    // No toast notification - chip appearing in chat is visual feedback enough
    if (onDocumentDragToChat) {
      onDocumentDragToChat(activeTabId, activeTab.title, activeTab.type)
    }
  }, [activeTabId, currentDocumentPath, openTabs, onDocumentDragToChat])

  return {
    // State
    currentDocumentPath,
    documentSaveState,
    undoState,
    moveDialogOpen,
    renameDialogOpen,
    deleteDialogOpen,
    documentToDelete,

    // Setters
    setCurrentDocumentPath,
    setMoveDialogOpen,
    setRenameDialogOpen,
    setDeleteDialogOpen,

    // Handlers
    handleRenameClick,
    handleRenameConfirm,
    handleMove,
    handleDeleteClick,
    handleDelete,
    handleShowFileLocation,
    handleManualSave,
    completeManualSave,
    handleUndo,
    handleExportPDF,
    handleAddToChatClick,
  }
}

